import { useState } from "react";
import {
  AlertTriangle,
  ExternalLink,
  HelpCircle,
  LifeBuoy,
  RotateCcw,
} from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogBody,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Spinner } from "@/components/ui/spinner";
import { formatDateTime } from "@/lib/utils";
import { describeRetryProvisioning } from "@/lib/retryProvisioning";
import type { ResourceStatus } from "@/types/admin";

interface RetryProvisioningDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  resourceName: string;
  status: ResourceStatus;
  tenantName?: string | null;
  /** When the VM was ordered — roughly when the timed-out create request went out. */
  createdAt: string;
  /**
   * No provider id is recorded, so the last create request may have built a machine WiCloud never
   * heard back about (server-computed `machineMayExist`). The dialog then warns, and Retry waits for
   * the operator to confirm the infrastructure team found no machine.
   */
  mayAlreadyExist: boolean;
  /**
   * The provider id this VM records, shown beside the "machine is gone" option. That option is
   * offered whenever an id is recorded (`mayAlreadyExist` false), also when this is unknown here.
   */
  recordedVmUuid?: string | null;
  /**
   * Opens the "Update resource by UUID" page instead — the answer when the machine does exist.
   * Optional: without it the button is hidden rather than sent nowhere.
   */
  onUpdateByUuidInstead?: () => void;
  /** True while the request runs; the dialog stays open and cannot be dismissed until it settles. */
  isPending: boolean;
  /**
   * `recreateMissingVm` is the operator's tick to drop a recorded machine the infrastructure no
   * longer has; always false for a VM with no provider id.
   */
  onConfirm: (options: { recreateMissingVm: boolean }) => void;
}

/**
 * Confirmation for "Retry provisioning". Says what the retry will do for this VM's status, with the
 * rules behind it one click away in "How this works" and the full guide in its own tab.
 */
export function RetryProvisioningDialog({
  open,
  onOpenChange,
  resourceName,
  status,
  tenantName,
  createdAt,
  mayAlreadyExist,
  recordedVmUuid,
  onUpdateByUuidInstead,
  isPending,
  onConfirm,
}: RetryProvisioningDialogProps) {
  // The page mounts this dialog only while it is open, so every opening starts unticked: a tick left
  // over from an earlier visit is not a check.
  const [confirmedNoMachine, setConfirmedNoMachine] = useState(false);
  const canRetry = !isPending && (!mayAlreadyExist || confirmedNoMachine);

  // A recorded machine is only re-checked. When it is gone the operator ticks this instead, and the
  // server drops the id (after asking the infrastructure itself) so a new machine is created.
  const hasRecordedMachine = !mayAlreadyExist;
  const [recreateMissingVm, setRecreateMissingVm] = useState(false);

  return (
    <Dialog
      open={open}
      // Escape and the corner X are ignored while the request runs, so the outcome is not reported
      // behind a dialog that already vanished.
      onOpenChange={(next) => {
        if (next || !isPending) onOpenChange(next);
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Retry provisioning of {resourceName}?</DialogTitle>
        </DialogHeader>

        <DialogBody className="space-y-4">
          <DialogDescription>
            {describeRetryProvisioning(status)}
          </DialogDescription>
          {mayAlreadyExist && (
            <PossibleMachineWarning
              resourceName={resourceName}
              tenantName={tenantName}
              createdAt={createdAt}
              confirmed={confirmedNoMachine}
              onConfirmedChange={setConfirmedNoMachine}
              onUpdateByUuidInstead={
                onUpdateByUuidInstead
                  ? () => {
                      onOpenChange(false);
                      onUpdateByUuidInstead();
                    }
                  : undefined
              }
              disabled={isPending}
            />
          )}
          {hasRecordedMachine && (
            <MissingMachineOption
              recordedVmUuid={recordedVmUuid}
              checked={recreateMissingVm}
              onCheckedChange={setRecreateMissingVm}
              disabled={isPending}
            />
          )}
        </DialogBody>

        <DialogFooter className="gap-2">
          <RetryHelp />
          <Button
            variant="outline"
            disabled={isPending}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            disabled={!canRetry}
            onClick={() =>
              onConfirm({
                recreateMissingVm: hasRecordedMachine && recreateMissingVm,
              })
            }
          >
            {isPending ? (
              <Spinner className="mr-2 h-4 w-4" />
            ) : (
              <RotateCcw className="mr-2 h-4 w-4" />
            )}
            Retry provisioning
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface PossibleMachineWarningProps {
  resourceName: string;
  tenantName?: string | null;
  createdAt: string;
  confirmed: boolean;
  onConfirmedChange: (confirmed: boolean) => void;
  /** Absent on the list row action, which has no recovery target of its own — hides the button. */
  onUpdateByUuidInstead?: () => void;
  disabled: boolean;
}

/**
 * The one case a retry can build a second machine: no provider id is recorded, so the last create
 * request may have been carried out unseen, and a retry sends a new one. Says what to check, where to
 * go when the machine exists, and holds Retry until the operator confirms it does not.
 */
function PossibleMachineWarning({
  resourceName,
  tenantName,
  createdAt,
  confirmed,
  onConfirmedChange,
  onUpdateByUuidInstead,
  disabled,
}: PossibleMachineWarningProps) {
  const owner = tenantName ? `, tenant ${tenantName}` : "";

  return (
    <Alert variant="destructive">
      <AlertTriangle className="h-4 w-4" />
      <AlertTitle>Check first: this VM may already exist</AlertTitle>
      <AlertDescription className="space-y-3">
        <p>
          <b>No provider id is recorded</b> for this VM. Its last create request
          may still have been carried out — after a timeout, a dropped
          connection or a server error the provider can build the machine
          without WiCloud ever hearing back. A retry sends a <b>new</b> create
          request — if the machine exists, the customer ends up with <b>two</b>.
        </p>
        <ol className="list-decimal space-y-1 pl-5">
          <li>
            Ask the infrastructure team whether a machine exists for{" "}
            <b>{resourceName}</b> (
            {`requested ${formatDateTime(createdAt)}${owner}`}).
          </li>
          <li>
            <b>It exists</b>: do not retry. Link it with Update resource by UUID
            {onUpdateByUuidInstead ? "" : " (open this VM's detail page)"}.
          </li>
          <li>
            <b>It does not</b>: tick the box below and retry.
          </li>
        </ol>
        {/* Absent only when the caller passes no way to the recovery page — the list item above
            then sends the operator to the detail page instead. */}
        {onUpdateByUuidInstead && (
          <Button
            variant="outline"
            size="sm"
            disabled={disabled}
            onClick={onUpdateByUuidInstead}
          >
            <LifeBuoy className="mr-2 h-4 w-4" />
            Update by UUID instead
          </Button>
        )}
        <label className="flex cursor-pointer items-start gap-2 text-foreground">
          <Checkbox
            checked={confirmed}
            disabled={disabled}
            onCheckedChange={(value) => onConfirmedChange(value === true)}
            className="mt-0.5"
          />
          <span>
            The infrastructure team confirmed no machine exists for this VM.
          </span>
        </label>
      </AlertDescription>
    </Alert>
  );
}

interface MissingMachineOptionProps {
  recordedVmUuid?: string | null;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled: boolean;
}

/**
 * The other dead end: a provider id is recorded, so a retry only re-checks that machine, but the
 * infrastructure no longer has it and every check comes back "not found" until the retry times out.
 * Ticking this asks the server to drop the id and create a new machine. The server asks the
 * infrastructure itself first, so the tick alone never drops a machine that still exists.
 */
function MissingMachineOption({
  recordedVmUuid,
  checked,
  onCheckedChange,
  disabled,
}: MissingMachineOptionProps) {
  return (
    <Alert>
      <AlertTriangle className="h-4 w-4" />
      <AlertTitle>Is the recorded machine gone?</AlertTitle>
      <AlertDescription className="space-y-3">
        <p>
          This VM records{" "}
          {recordedVmUuid ? (
            <>
              machine <code className="break-all">{recordedVmUuid}</code>
            </>
          ) : (
            "a provider id"
          )}
          , so a retry only re-checks that machine. If the infrastructure no
          longer has it, the retry checks for about 5 minutes and fails again.
        </p>
        <label className="flex cursor-pointer items-start gap-2 text-foreground">
          <Checkbox
            checked={checked}
            disabled={disabled}
            onCheckedChange={(value) => onCheckedChange(value === true)}
            className="mt-0.5"
          />
          <span>
            Drop this machine id and create a <b>new</b> machine. WiCloud asks
            the infrastructure first: the id is dropped only when it answers
            that the machine does not exist. If the machine is still there, or
            there is no answer, the retry is refused and nothing changes.
          </span>
        </label>
      </AlertDescription>
    </Alert>
  );
}

/**
 * What the retry does, when it is offered and what it leaves alone, opened in place. Kept short on
 * purpose: the refusals, in the order the server checks them and with their codes, are in
 * `/docs/retry-provisioning.html` — the error toast already names the one that applied.
 *
 * Only Error is retryable since 2026-09-28; Pending and Provisioning are left to
 * ProvisioningRecoveryJob. If those branches come back, the guide's retired cards say what to add.
 */
function RetryHelp() {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="sm:mr-auto">
          <HelpCircle className="mr-2 h-4 w-4" />
          How this works
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="top"
        className="max-h-[70vh] w-[26rem] space-y-3 overflow-y-auto text-sm"
      >
        <p className="text-muted-foreground">
          Starts the build of this one VM again, at once. In one save the VM and
          its disks and networks left in Error or Pending go back to
          Provisioning, a Failed order goes back to Processing and the error the
          customer sees is cleared; then the build job is queued.
        </p>

        <div className="space-y-1.5">
          <p className="font-medium">Offered when</p>
          <ul className="space-y-1 text-muted-foreground">
            <li>· the VM is in Error left by its build and has never run</li>
            <li>· it was created less than 24 hours ago</li>
            <li>
              · its new-service order is paid, not refunded, and still has a
              usable billing cycle
            </li>
          </ul>
          <p className="text-muted-foreground">
            Every admin sees the item; only a SuperAdmin can press it.
          </p>
        </div>

        <div className="space-y-1.5">
          <p className="font-medium">The build job then</p>
          <pre className="overflow-x-auto rounded-md border bg-muted/40 p-2 font-mono text-[11px] leading-snug text-muted-foreground">
            {`provider id recorded ─► re-check that machine (~5 min)
                        ─► Running, or Error again
"Drop this machine id" ticked
   └─ infra: no such VM ─► id dropped ─► create a new one
   └─ VM still there / no answer ─► refused, nothing written
no provider id ─► claim (10-min lease) ─► create request
   (one run only, even with a double click or a retry)`}
          </pre>
          <p className="text-muted-foreground">
            While the VM stays Provisioning, the recovery job queues the build
            again every 10 minutes. That is harmless: the claim and the recorded
            id stop a second machine.
          </p>
        </div>

        <div className="space-y-1.5">
          <p className="font-medium">No provider id?</p>
          <p className="text-muted-foreground">
            The last create request may have built a machine anyway. Ask the
            infrastructure team first: if it exists, use Update resource by UUID
            — retrying would build a second one.
          </p>
        </div>

        <div className="space-y-1.5">
          <p className="font-medium">It never</p>
          <ul className="space-y-1 text-muted-foreground">
            <li>· extends the expiry — correct it by hand if days were lost</li>
            <li>· emails the customer, moves money or changes a price</li>
          </ul>
          <p className="text-muted-foreground">
            A retry writes an audit row the customer does not see. Refused? The
            error message says why; nothing was written.
          </p>
        </div>

        <Button variant="outline" size="sm" asChild className="w-full">
          <a
            href="/docs/retry-provisioning.html"
            target="_blank"
            rel="noreferrer"
          >
            Full guide
            <ExternalLink className="ml-2 h-3.5 w-3.5" />
          </a>
        </Button>
      </PopoverContent>
    </Popover>
  );
}
